import {useCallback, useRef, useState} from 'react'
import {View} from 'react-native'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'
import {useQueryClient} from '@tanstack/react-query'

import {type NavigationProp} from '#/lib/routes/types'
import {logger} from '#/logger'
import {useClassroomActor, useCreatePermission} from '#/state/classrooms'
import {useCreateClassroomMutation} from '#/state/queries/classrooms'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {
  ClassroomFields,
  type ClassroomFormValues,
  effectiveLevel,
  effectiveLevelNameType,
  effectiveParentsCanJoin,
  EMPTY_CLASSROOM_FORM,
  levelNeedsGrade,
} from './ClassroomFields'
import {attachClassroomCover} from './publishClassroomFace'
import {showClassroomError} from './showClassroomError'

/**
 * The one place a creator makes a classroom.
 *
 * Reachable from the profile, between Likes and Feeds — not from a global menu,
 * because a classroom belongs to the account that made it and that is where its
 * own content lives. Every creator-side account may make one; a learner or a
 * parent is told why not rather than shown a form that cannot submit.
 */
export function CreateClassroomDialog({
  control,
}: {
  control: DialogControlProps
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const actor = useClassroomActor()
  const permission = useCreatePermission()
  const agent = useAgent()
  const create = useCreateClassroomMutation()
  const queryClient = useQueryClient()

  const [values, setValues] =
    useState<ClassroomFormValues>(EMPTY_CLASSROOM_FORM)
  // A ref, not state: two presses can land in the same tick, before React has
  // re-rendered, so the second one still sees the `canSubmit` this render closed
  // over. Without the latch a double tap creates two classrooms with two join
  // codes and only the last one is visible.
  const inFlightRef = useRef(false)

  const onChange = useCallback(
    <K extends keyof ClassroomFormValues>(
      key: K,
      value: ClassroomFormValues[K],
    ) => setValues(prev => ({...prev, [key]: value})),
    [],
  )

  const canSubmit =
    permission.allowed &&
    !create.isPending &&
    !!actor &&
    values.name.trim().length > 0 &&
    /*
     * A classroom with no level is allowed — it is the one open to every
     * learner — but a shared level that teaches years has to name its year, and
     * a SYSTEM level has to name itself.
     */
    (effectiveLevelNameType(values) === 'SYSTEM'
      ? effectiveLevel(values).trim().length > 0
      : !levelNeedsGrade(values) || values.grade.length > 0)

  const reset = useCallback(() => setValues(EMPTY_CLASSROOM_FORM), [])

  const onClose = useCallback(() => reset(), [reset])

  const onSubmit = async () => {
    if (!canSubmit || !actor || inFlightRef.current) return
    inFlightRef.current = true
    try {
      const classroom = await create.mutateAsync({
        name: values.name,
        description: values.description,
        visibility: values.visibility,
        level: effectiveLevel(values),
        grade: values.grade || undefined,
        levelNameType: effectiveLevelNameType(values),
        /*
         * The two answers that are only about the broad levels (`University`,
         * `Other`): the school's own name for what it teaches, and whether the
         * room takes learners from every country. The form only offers them
         * where they mean something.
         */
        levelLabel: values.levelLabel || undefined,
        country: actor.country ?? undefined,
        international: values.international || undefined,
        parentsCanJoin: effectiveParentsCanJoin(values),
        interests: values.interests,
        owner: actor,
      })
      /*
       * The cover is optional, and an empty string is *no cover* rather than a
       * picture of nothing. It needs the room's id (the public face is keyed by
       * it), so it is attached once the room exists; the room stands either
       * way, so a failure here is said but does not undo the create.
       */
      if (values.cover) {
        await attachClassroomCover(
          agent,
          queryClient,
          classroom,
          values.cover,
        ).catch(error => {
          logger.error('Failed to attach a classroom cover', {
            safeMessage: error,
          })
          showClassroomError(i18n, error)
        })
      }
      Toast.show(l`Classroom created`)
      // Close before navigating: leaving the dialog mounted and tappable behind
      // the new screen is what let the extra presses through.
      control.close(() => {
        // Released only once the dialog is gone, so a press during the close
        // animation cannot make a second room.
        inFlightRef.current = false
        reset()
        /*
         * The room opens at the address the server answers to: the school's
         * handle and the room's own handle.
         */
        navigation.navigate('Classroom', {
          name: classroom.ownerHandle,
          rkey: classroom.handle,
        })
      })
    } catch (error) {
      inFlightRef.current = false
      showClassroomError(i18n, error)
    }
  }

  return (
    <Dialog.Outer control={control} onClose={onClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Create a classroom`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>{l`Create a classroom`}</Text>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {l`A classroom is where your students, and their parents, gather. Students join with a code or a QR you hand out.`}
          </Text>

          {!permission.allowed && permission.reason ? (
            <Text
              testID="classroomCreateBlocked"
              style={[a.text_md, {color: t.palette.negative_500}]}>
              {permission.reason}
            </Text>
          ) : null}

          <ClassroomFields
            values={values}
            onChange={onChange}
            country={actor?.country}
          />

          <Button
            testID="classroomCreateSubmit"
            label={l`Create classroom`}
            size="large"
            color="primary"
            disabled={!canSubmit}
            onPress={() => void onSubmit()}>
            <ButtonText>{l`Create classroom`}</ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
